/**
 * FinishingProductionEntryRegister · P17-UAT-16B-3R
 *
 * READ-ONLY register for Finishing Production Entries, mirroring the existing
 * Production Entry Register architecture (layout, filters, table, View modal,
 * status/V2 presentation, print/export). Authoritative source:
 * GET /finishing-production-entries (production_entries · stage=Finishing).
 *
 * Register = navigation/control surface only. Viewing/opening causes ZERO V2
 * mutation. Posting happens exclusively through the existing 16B-3 workflow on
 * the Department Production Entry → Finishing screen (Save & Post Finishing Stock).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DocumentNumberDisplay from "@/components/DocumentNumberDisplay";
import { RowActions } from "@/components/RowActions";
import { exportToCSV } from "@/lib/csv";
import {
  MagnifyingGlassIcon, ArrowsClockwiseIcon, DownloadSimpleIcon, SparkleIcon,
  SlidersHorizontalIcon, XIcon,
} from "@phosphor-icons/react";

const ACCENT = "#34d399";
const TID = "finishing-register";
const todayISO = () => new Date().toISOString().slice(0, 10);
const monthStartISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const fmtQty = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 }));
const dtOf = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleString([], { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
};
const timeOf = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const STATUS_STYLES = {
  POSTED: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  DRAFT: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",
  REVERSED: "bg-rose-500/15 text-rose-300 border-rose-500/40",
};
const V2_STYLES = {
  "V2 POSTED": "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  "NOT POSTED": "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",
  "V2 REVERSED": "bg-rose-500/15 text-rose-300 border-rose-500/40",
};
const Badge = ({ text, styles, testId }) => (
  <span data-testid={testId} className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wide ${styles[text] || "bg-zinc-700 text-zinc-200 border-zinc-600"}`}>{text || "—"}</span>
);

export default function FinishingProductionEntryRegister() {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canReverse = hasPerm("inventory:edit");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewRow, setViewRow] = useState(null);
  const [reverseRow, setReverseRow] = useState(null);
  const [reverseReason, setReverseReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [showMore, setShowMore] = useState(false);
  // REV-B Issue-1 · operational posting from View (no Edit mode): preview + destination + post
  const canPost = hasPerm("inventory:edit");
  const [pv, setPv] = useState(null);
  const [pvOpts, setPvOpts] = useState([]);
  const [pvDestId, setPvDestId] = useState("");
  const [posting, setPosting] = useState(false);

  const emptyFilters = {
    fromDate: monthStartISO(), toDate: todayISO(),
    entryNo: "", wo: "", order: "", product: "", machine: "", status: "ALL", v2: "ALL", sourceBatch: "",
  };
  const [draft, setDraft] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);

  const load = () => {
    setLoading(true);
    return api.get("/finishing-production-entries")
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error("Failed to load Finishing production entries"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const f = applied;
    const inRange = (d) => (!f.fromDate || (d || "") >= f.fromDate) && (!f.toDate || (d || "") <= f.toDate);
    const like = (v, q) => !q || (v || "").toLowerCase().includes(q.toLowerCase());
    return rows.filter((r) =>
      inRange(r.entry_date) &&
      like(r.entry_no, f.entryNo) && like(r.wo_no, f.wo) && like(r.so_eso, f.order) &&
      like(r.product_code, f.product) && like(r.machine, f.machine) && like(r.source_batch, f.sourceBatch) &&
      (f.status === "ALL" || r.status === f.status) &&
      (f.v2 === "ALL" || r.v2_posting_status === f.v2));
  }, [rows, applied]);

  const applyFilters = () => setApplied(draft);
  const resetFilters = () => { setDraft(emptyFilters); setApplied(emptyFilters); };

  const openContinue = (r) => {
    // Navigation only — restores the saved draft into the Finishing screen.
    navigate(`/production/finishing-dyeing-entry?tab=finishing&finishingPe=${encodeURIComponent(r.id)}`);
  };

  // REV-B · when a DRAFT PE is opened in View, load its fail-closed preflight +
  // valid next-process destination options so posting can happen right here.
  useEffect(() => {
    if (!viewRow || viewRow.status !== "DRAFT") { setPv(null); setPvOpts([]); setPvDestId(""); return; }
    let alive = true;
    setPv(null);
    Promise.all([
      api.get(`/production-entries/${viewRow.id}/finishing-preview`).then(r => r.data).catch(() => null),
      api.get(`/finishing/destination-options?product_code=${encodeURIComponent(viewRow.product_code || "")}`).then(r => r.data).catch(() => null),
    ]).then(([prev, opts]) => {
      if (!alive) return;
      setPv(prev);
      const o = (opts && opts.ok ? opts.options : []) || [];
      setPvOpts(o);
      setPvDestId(prev2 => (o.length === 1 ? o[0].godown_id : ""));
    });
    return () => { alive = false; };
  }, [viewRow]);

  const submitPost = async () => {
    if (!pvDestId) return toast.error("Select a destination Packing WIP godown");
    setPosting(true);
    try {
      await api.post(`/production-entries/${viewRow.id}/finishing-post`, { dest_godown_id: pvDestId });
      toast.success(`${viewRow.entry_no} posted · Finishing stock committed`);
      setViewRow(null); await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    } finally { setPosting(false); }
  };

  const submitReverse = async () => {
    if (reverseReason.trim().length < 4) return toast.error("Reversal reason required (min 4 characters)");
    setBusy(true);
    try {
      const { data } = await api.post(`/production-entries/${reverseRow.id}/finishing-reverse`, { reason: reverseReason.trim() });
      toast.success(`${reverseRow.entry_no} reversed · ${(data.reversed || []).length} leg(s)`);
      setReverseRow(null); setReverseReason(""); await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reversal failed");
    } finally { setBusy(false); }
  };

  const csvColumns = [
    { key: "entry_no", label: "Entry No" }, { key: "entry_date", label: "Production Date" },
    { key: "created_at", label: "Time", fmt: timeOf }, { key: "status", label: "Status" },
    { key: "wo_no", label: "WO" }, { key: "so_eso", label: "SO/ESO Reference" },
    { key: "product_code", label: "Product Code" }, { key: "machine", label: "Machine" },
    { key: "source_godown", label: "Source Godown" }, { key: "source_batch", label: "Source Batch" },
    { key: "input_kg", label: "Input Weight kg" }, { key: "input_m", label: "Input Length m" },
    { key: "output_kg", label: "Output Weight kg" },
    { key: "process_loss_kg", label: "Process Loss kg" }, { key: "loss_m", label: "Process Loss m" },
    { key: "metres", label: "Production Length m" },
    { key: "bom_standard_gpm", label: "BOM g/m" }, { key: "dest_godown", label: "Destination Godown" },
    { key: "output_batch", label: "Output Batch", fmt: (v) => v || "Not Generated" },
    { key: "created_by", label: "Created By" }, { key: "updated_at", label: "Last Updated", fmt: dtOf },
    { key: "v2_posting_status", label: "V2 Posting Status" },
  ];
  const onExport = () => {
    if (!filtered.length) return toast.error("Nothing to export");
    exportToCSV(`Finishing_Production_Entry_Register_${applied.fromDate}_${applied.toDate}.csv`, filtered, csvColumns);
  };

  const FField = ({ label, k, placeholder }) => (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] uppercase tracking-wider text-zinc-400">{label}</label>
      <input data-testid={`${TID}-filter-${k}`} value={draft[k]} placeholder={placeholder || ""}
        onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
        onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
        className="rounded-md border border-zinc-700 bg-zinc-900 px-2.5 py-1.5 text-sm text-zinc-100 outline-none focus:border-zinc-500" />
    </div>
  );

  return (
    <div data-testid={TID} className="space-y-6 p-1">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl border" style={{ borderColor: `${ACCENT}55`, background: `${ACCENT}14` }}>
            <SparkleIcon size={22} weight="bold" style={{ color: ACCENT }} />
          </div>
          <div>
            <h1 className="text-xl font-black tracking-tight text-white" data-testid={`${TID}-title`}>Finishing Production Entry Register</h1>
            <p className="text-xs text-zinc-400">Read-only control surface · WO is production authority · SO/ESO is reference</p>
          </div>
        </div>
        <button data-testid={`${TID}-export-csv`} onClick={onExport} className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-100 hover:border-zinc-500">
          <DownloadSimpleIcon size={16} weight="bold" /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-zinc-400">From Date</label>
            <input type="date" data-testid={`${TID}-filter-fromDate`} value={draft.fromDate}
              onChange={(e) => setDraft((d) => ({ ...d, fromDate: e.target.value }))}
              className="h-9 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 text-sm text-zinc-100 outline-none focus:border-zinc-500" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-zinc-400">To Date</label>
            <input type="date" data-testid={`${TID}-filter-toDate`} value={draft.toDate}
              onChange={(e) => setDraft((d) => ({ ...d, toDate: e.target.value }))}
              className="h-9 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 text-sm text-zinc-100 outline-none focus:border-zinc-500" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-zinc-400">Status</label>
            <select data-testid={`${TID}-filter-status`} value={draft.status}
              onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
              className="h-9 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 text-sm text-zinc-100 outline-none focus:border-zinc-500">
              {["ALL", "DRAFT", "POSTED", "REVERSED"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex min-w-[160px] flex-1 flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-zinc-400">WO</label>
            <input data-testid={`${TID}-filter-wo`} value={draft.wo} placeholder="WO-…"
              onChange={(e) => setDraft((d) => ({ ...d, wo: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
              className="h-9 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 text-sm text-zinc-100 outline-none focus:border-zinc-500" />
          </div>
          <button data-testid={`${TID}-apply`} onClick={applyFilters} className="inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-bold text-zinc-950" style={{ background: ACCENT }}>
            <MagnifyingGlassIcon size={16} weight="bold" /> Apply
          </button>
          <button data-testid={`${TID}-reset`} onClick={resetFilters} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900 px-3 text-sm font-semibold text-zinc-100 hover:border-zinc-500">
            <ArrowsClockwiseIcon size={16} weight="bold" /> Reset
          </button>
          <button data-testid={`${TID}-more-filters`} onClick={() => setShowMore((v) => !v)} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900 px-3 text-sm font-semibold text-zinc-100 hover:border-zinc-500">
            <SlidersHorizontalIcon size={16} weight="bold" /> More
          </button>
        </div>
        {showMore && (
          <div data-testid={`${TID}-more-panel`} className="mt-3 grid grid-cols-2 gap-3 border-t border-zinc-800 pt-3 sm:grid-cols-4">
            <FField label="Entry No" k="entryNo" placeholder="PE-…" />
            <FField label="SO / ESO" k="order" placeholder="ESO-…" />
            <FField label="Product" k="product" placeholder="Product code" />
            <FField label="Machine" k="machine" placeholder="F101…" />
            <FField label="Source Batch" k="sourceBatch" placeholder="WO-…" />
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase tracking-wider text-zinc-400">V2 Posting</label>
              <select data-testid={`${TID}-filter-v2`} value={draft.v2}
                onChange={(e) => setDraft((d) => ({ ...d, v2: e.target.value }))}
                className="rounded-md border border-zinc-700 bg-zinc-900 px-2.5 py-1.5 text-sm text-zinc-100 outline-none focus:border-zinc-500">
                {["ALL", "NOT POSTED", "V2 POSTED", "V2 REVERSED"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-zinc-800">
        <table className="min-w-[1700px] w-full text-sm" data-testid={`${TID}-table`}>
          <thead>
            <tr className="border-b border-zinc-800 bg-zinc-900/70 text-left text-[11px] uppercase tracking-wider text-zinc-400">
              {["Entry No", "Date", "Time", "Status", "WO", "SO/ESO", "Product", "Machine", "Source Godown", "Source Batch", "Input kg", "Output kg", "Loss kg", "Metres", "BOM g/m", "Dest Godown", "Output Batch", "V2 Posting", "Created By", "Last Updated", "Action"].map((h) => (
                <th key={h} className="px-3 py-2 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={21} className="px-3 py-10 text-center text-zinc-500">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={21} className="px-3 py-10 text-center text-zinc-500" data-testid={`${TID}-empty`}>No Finishing production entries found.</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} data-testid={`${TID}-row-${r.entry_no}`} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                <td className="px-3 py-2"><DocumentNumberDisplay value={r.entry_no} testId={`${TID}-entryno-${r.entry_no}`} /></td>
                <td className="px-3 py-2 whitespace-nowrap text-zinc-300">{r.entry_date || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap text-zinc-400">{timeOf(r.created_at)}</td>
                <td className="px-3 py-2"><Badge text={r.status} styles={STATUS_STYLES} testId={`${TID}-status-${r.entry_no}`} /></td>
                <td className="px-3 py-2"><DocumentNumberDisplay value={r.wo_no} /></td>
                <td className="px-3 py-2"><DocumentNumberDisplay value={r.so_eso} /></td>
                <td className="px-3 py-2 min-w-[240px] whitespace-normal break-words text-zinc-200" title={r.product_code}>{r.product_code || "—"}</td>
                <td className="px-3 py-2 text-zinc-300">{r.machine || "—"}</td>
                <td className="px-3 py-2 text-zinc-300">{r.source_godown || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs text-zinc-300">{r.source_batch || <span className="text-zinc-500">Not Selected</span>}</td>
                <td className="px-3 py-2 text-right font-semibold text-zinc-100">{fmtQty(r.input_kg)}</td>
                <td className="px-3 py-2 text-right font-semibold text-emerald-300">
                  {fmtQty(r.output_kg)}
                  <div className="text-[10px] font-normal text-zinc-500" data-testid={`${TID}-output-m-${r.entry_no}`}>{r.metres == null ? "— m" : `${fmtQty(r.metres)} m`}</div>
                </td>
                <td className="px-3 py-2 text-right text-amber-300">
                  {fmtQty(r.process_loss_kg)}
                  <div className="text-[10px] font-normal text-zinc-500" data-testid={`${TID}-loss-m-${r.entry_no}`}>{r.loss_m == null ? "— m" : `${fmtQty(r.loss_m)} m`}</div>
                </td>
                <td className="px-3 py-2 text-right text-zinc-300">{fmtQty(r.metres)}</td>
                <td className="px-3 py-2 text-right text-zinc-400">{fmtQty(r.bom_standard_gpm)}</td>
                <td className="px-3 py-2 text-sky-300">{r.dest_godown || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs text-zinc-300">{r.output_batch || <span className="text-zinc-500">Not Generated</span>}</td>
                <td className="px-3 py-2"><Badge text={r.v2_posting_status} styles={V2_STYLES} testId={`${TID}-v2-${r.entry_no}`} /></td>
                <td className="px-3 py-2 text-zinc-400">{r.created_by || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap text-zinc-400">{dtOf(r.updated_at)}</td>
                <td className="px-3 py-2">
                  <RowActions
                    testid={`${TID}-actions-${r.entry_no}`}
                    items={[
                      { label: "View", testid: `${TID}-view-${r.entry_no}`, onClick: () => setViewRow(r) },
                      { label: "Open / Continue", tone: "approve", testid: `${TID}-open-${r.entry_no}`, show: r.status === "DRAFT", onClick: () => openContinue(r) },
                      { label: "Reverse", tone: "reverse", testid: `${TID}-reverse-${r.entry_no}`, show: r.status === "POSTED" && canReverse, onClick: () => { setReverseRow(r); setReverseReason(""); } },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* View modal — frozen snapshot for POSTED */}
      {viewRow && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4" data-testid={`${TID}-view-modal`} onClick={() => setViewRow(null)}>
          <div className="mt-10 w-full max-w-3xl rounded-2xl border border-zinc-700 bg-zinc-950 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Finishing Production Entry {viewRow.status === "POSTED" ? "· FROZEN SNAPSHOT" : ""}</div>
                <h2 className="text-lg font-black text-white" data-testid={`${TID}-view-title`}>{viewRow.entry_no}</h2>
              </div>
              <div className="flex items-center gap-2">
                <Badge text={viewRow.status} styles={STATUS_STYLES} />
                <Badge text={viewRow.v2_posting_status} styles={V2_STYLES} />
                <button data-testid={`${TID}-view-close`} onClick={() => setViewRow(null)} className="rounded-lg border border-zinc-700 bg-zinc-900 p-2 text-zinc-300 hover:border-zinc-500"><XIcon size={16} /></button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
              {[
                ["Entry Date", viewRow.entry_date], ["Entry Time", timeOf(viewRow.created_at)],
                ["WO (authority)", viewRow.wo_no], ["SO / ESO (reference)", viewRow.so_eso],
                ["Product", viewRow.product_code], ["Machine", viewRow.machine],
                ["Source Godown", viewRow.source_godown], ["Source Batch", viewRow.source_batch || "Not Selected"],
                ["Input kg", fmtQty(viewRow.input_kg)], ["Input m", viewRow.input_m == null ? "— m" : `${fmtQty(viewRow.input_m)} m`],
                ["Output kg", fmtQty(viewRow.output_kg)], ["Output m", viewRow.metres == null ? "— m" : `${fmtQty(viewRow.metres)} m`],
                ["Process Loss kg", fmtQty(viewRow.process_loss_kg)], ["Process Loss m", viewRow.loss_m == null ? "— m" : `${fmtQty(viewRow.loss_m)} m`],
                ["BOM Standard g/m", fmtQty(viewRow.bom_standard_gpm)], ["Destination Godown", viewRow.dest_godown],
                ["Output Batch", viewRow.output_batch || "Not Generated"], ["Generation", viewRow.generation >= 0 ? `G${viewRow.generation}` : "—"],
                ["V2 Posting", viewRow.v2_posting_status], ["Created By", viewRow.created_by || "—"],
                ["Posted By", viewRow.posted_by || "—"], ["Posted At", dtOf(viewRow.posted_at)],
              ].map(([k, v]) => (
                <div key={k}><div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div><div className="text-sm font-semibold text-zinc-100 break-words">{v ?? "—"}</div></div>
              ))}
            </div>
            {viewRow.status === "DRAFT" && (
              <div className="mt-5 rounded-xl border border-emerald-800/50 bg-emerald-950/20 p-4" data-testid={`${TID}-post-panel`}>
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-[11px] font-black uppercase tracking-widest text-emerald-300">Operational Posting (no Edit mode)</div>
                  {pv?.header?.next_process || pv?.next_process ? (
                    <span className="text-[10px] text-zinc-400">Next process: <span className="font-mono text-sky-300">{(pv.next_process || "").replace(/_/g, " ")}</span></span>
                  ) : null}
                </div>
                {pv && !pv.postable && (pv.reasons || []).length > 0 && (
                  <div className="mb-3 rounded-lg border border-amber-700/40 bg-amber-950/30 p-2.5 text-xs text-amber-300" data-testid={`${TID}-post-blocked`}>
                    {pv.reasons[0]}
                  </div>
                )}
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex min-w-[260px] flex-1 flex-col gap-1">
                    <label className="text-[10px] uppercase tracking-wider text-zinc-400">Destination (Packing WIP) — system-resolved</label>
                    {pvOpts.length === 1 ? (
                      <div data-testid={`${TID}-post-dest-fixed`}
                        className="flex h-9 items-center rounded-md border border-emerald-700/50 bg-zinc-900 px-2.5 text-sm font-mono text-emerald-200">
                        {pvOpts[0].godown_code} · {pvOpts[0].godown_name}
                      </div>
                    ) : (
                      <select data-testid={`${TID}-post-dest-select`} value={pvDestId} onChange={(e) => setPvDestId(e.target.value)}
                        disabled={!canPost || pvOpts.length === 0}
                        className="h-9 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 text-sm text-sky-200 outline-none focus:border-emerald-500 disabled:opacity-50">
                        <option value="">{pvOpts.length ? "— Select Packing WIP —" : "No valid destination"}</option>
                        {pvOpts.map((o) => <option key={o.godown_id} value={o.godown_id}>{o.godown_code} · {o.godown_name}</option>)}
                      </select>
                    )}
                  </div>
                  <button data-testid={`${TID}-post-confirm`} onClick={submitPost}
                    disabled={!canPost || posting || !pvDestId || !(pv && pv.postable)}
                    title={!canPost ? "Requires inventory:edit" : (!(pv && pv.postable) ? "Preconditions not met" : "")}
                    className="inline-flex h-9 items-center gap-1.5 rounded-lg px-4 text-sm font-black text-zinc-950 disabled:cursor-not-allowed disabled:opacity-40" style={{ background: ACCENT }}>
                    {posting ? "Posting…" : "Post Finishing"}
                  </button>
                </div>
                {!canPost && <div className="mt-2 text-[10px] text-zinc-500">Posting requires the inventory:edit permission.</div>}
              </div>
            )}
            {viewRow.status === "REVERSED" && (
              <div className="mt-4 rounded-lg border border-rose-700/40 bg-rose-950/30 p-3 text-sm text-rose-300" data-testid={`${TID}-view-reversed`}>
                Reversed by {viewRow.reversed_by || "—"} · {dtOf(viewRow.reversed_at)}{viewRow.reversal_reason ? ` · ${viewRow.reversal_reason}` : ""}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reverse confirmation — reuses the 16B-3 controlled reversal endpoint */}
      {reverseRow && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4" data-testid={`${TID}-reverse-modal`} onClick={() => !busy && setReverseRow(null)}>
          <div className="mt-16 w-full max-w-lg rounded-2xl border border-rose-700/50 bg-zinc-950 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-black text-white">Reverse Finishing Conversion</h2>
            <p className="mt-1 text-sm text-zinc-400">Calls the controlled 16B-3 reversal — restores source Woven-SFG, reverses output + wastage. Fails closed if the output batch was consumed downstream.</p>
            <div className="mt-4 grid grid-cols-2 gap-x-5 gap-y-2 rounded-lg border border-zinc-800 bg-zinc-900/50 p-3 text-sm">
              {[["Entry No", reverseRow.entry_no], ["WO", reverseRow.wo_no], ["Output kg", fmtQty(reverseRow.output_kg)], ["Output Batch", reverseRow.output_batch]].map(([k, v]) => (
                <div key={k}><span className="text-zinc-500">{k}: </span><span className="font-semibold text-zinc-100 break-words">{v || "—"}</span></div>
              ))}
            </div>
            <label className="mt-4 block text-[10px] uppercase tracking-wider text-zinc-400">Reversal Reason (min 4 chars)</label>
            <textarea data-testid={`${TID}-reverse-reason`} value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} rows={3}
              className="mt-1 w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-rose-500" placeholder="Why is this conversion being reversed?" />
            <div className="mt-4 flex justify-end gap-2">
              <button data-testid={`${TID}-reverse-cancel`} disabled={busy} onClick={() => setReverseRow(null)} className="rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-2 text-sm font-semibold text-zinc-100 hover:border-zinc-500 disabled:opacity-50">Cancel</button>
              <button data-testid={`${TID}-reverse-confirm`} disabled={busy || reverseReason.trim().length < 4} onClick={submitReverse} className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-bold text-white hover:bg-rose-500 disabled:opacity-50">{busy ? "Reversing…" : "Confirm Reversal"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
